import * as React from 'react';

import { UserContext } from '../Context/UserContext';
import { UserCallLogRepository } from '../../MedicareProAPI/Repositories/User/UserCallLogRepository';
import { WebAPI } from '../../MedicareProAPI/API/WebAPI';
import Loader from '../Components/Loader';
import { Utilities } from '../../MedicareProAPI/Utilities/Utilities';
import { Ind } from '../../MedicareProAPI/Entities/Ind/Ind';
import { IndRepository } from '../../MedicareProAPI/Repositories/Ind/IndRepository';
import { UserIntegrationOAuthRepository } from '../../MedicareProAPI/Repositories/User/UserIntegrationOAuthRepository';
import type { UserIntegrationOAuth } from '../../MedicareProAPI/Entities/User/UserIntegrationOAuth';
import type { UserIntegrationProvider } from '../../MedicareProAPI/Entities/User/UserIntegrationProvider';
import { AgencyNotificationPreferenceCollection } from '../../MedicareProAPI/Collections/Agency/AgencyNotificationPreferenceCollection';

export interface AgentLogsWebhookPanelProps {}

export type AgentLogsWebhook = {
  accessTokenStatus?: boolean;
  webhookStatus?: boolean;
  webhookData?: any;
  provider: UserIntegrationProvider;
};

export function DialpadWebhookReport(data: any): React.JSX.Element {
  const webhookData = data.webhookData;

  return (
    <>
      {webhookData &&
      webhookData.calls &&
      webhookData.calls.items &&
      webhookData.calls.items.length === 3 ? (
        <p className="alert alert-info mb-0 mt-1">
          Call Webhooks Configured Correctly.
          <ul>
            {webhookData.calls.items.map((item) => {
              return <li>{item.call_states.join(' ')}</li>;
            })}
          </ul>
        </p>
      ) : (
        <p className="alert alert-warning mb-0 mt-1">Call Webhooks Not Configured Correctly.</p>
      )}
      {webhookData &&
      webhookData.sms &&
      webhookData.sms.items &&
      webhookData.sms.items.length === 1 ? (
        <p className="alert alert-info mb-0 mt-1">SMS Webhooks Configured Correctly.</p>
      ) : (
        <p className="alert alert-warning mb-0 mt-1">SMS Webhooks Not Configured Correctly.</p>
      )}
    </>
  );
}

export function RingCentralWebhookReport(data: any): React.JSX.Element {
  const webhookData = data.webhookData;

  return (
    <>
      {webhookData &&
      webhookData.webhooks &&
      webhookData.webhooks.records &&
      webhookData.webhooks.records.length === 2 ? (
        <p className="alert alert-info mb-0 mt-1">Call Webhooks Configured Correctly.</p>
      ) : (
        <p className="alert alert-warning mb-0 mt-1">Call Webhooks Not Configured Correctly.</p>
      )}
      {webhookData &&
      webhookData.webhooks &&
      webhookData.webhooks.records &&
      webhookData.webhooks.records.length === 2 ? (
        <p className="alert alert-info mb-0 mt-1">SMS Webhooks Configured Correctly.</p>
      ) : (
        <p className="alert alert-warning mb-0 mt-1">SMS Webhooks Not Configured Correctly.</p>
      )}
    </>
  );
}

export function PhonecomWebhookReport(data: any): React.JSX.Element {
  const webhookData = data.webhookData;

  return (
    <>
      {webhookData &&
      webhookData.callbacks &&
      webhookData.callbacks.items &&
      webhookData.callbacks.items.length === 2 &&
      webhookData &&
      webhookData.listeners &&
      webhookData.listeners.items &&
      webhookData.listeners.items.length === 2 ? (
        <p className="alert alert-info mb-0 mt-1">Call Webhooks Configured Correctly.</p>
      ) : (
        <p className="alert alert-warning mb-0 mt-1">Call Webhooks Not Configured Correctly.</p>
      )}
      {webhookData &&
      webhookData.callbacks &&
      webhookData.callbacks.items &&
      webhookData.callbacks.items.length === 2 &&
      webhookData &&
      webhookData.listeners &&
      webhookData.listeners.items &&
      webhookData.listeners.items.length === 2 ? (
        <p className="alert alert-info mb-0 mt-1">SMS Webhooks Configured Correctly.</p>
      ) : (
        <p className="alert alert-warning mb-0 mt-1">SMS Webhooks Not Configured Correctly.</p>
      )}
    </>
  );
}

export function AgentLogsWebhookSingle(props: AgentLogsWebhook): React.JSX.Element {
  const [userIntegrationOAuth, setUserIntegrationOAuth] =
    React.useState<UserIntegrationOAuth>(null);
  const [userIntegrationOAuthLoading, setUserIntegrationOAuthLoading] =
    React.useState<boolean>(true);
  const [webhookData, setWebhookData] = React.useState<any>(null);
  const [webhookDataLoading, setWebhookDataLoading] = React.useState<boolean>(true);

  const provider = props.provider;

  const userData = React.useContext(UserContext);

  const webAPI = new WebAPI();
  webAPI.setAccessToken(userData.userAccessToken);

  const userIntegrationOAuthRepository = new UserIntegrationOAuthRepository();
  userIntegrationOAuthRepository.setAccessToken(userData.userAccessToken);

  React.useEffect(() => {
    setWebhookDataLoading(true);
    setUserIntegrationOAuthLoading(true);

    webAPI
      .getUserIntegrationWebhooks(userData.userId, provider.getReference())
      .then((webhookData) => {
        setWebhookDataLoading(false);
        setWebhookData(webhookData);
      });

    webAPI.validateUserIntegrationOAuth(provider.getReference()).then((oauthData) => {});

    userIntegrationOAuthRepository
      .getUserIntegrationOAuth(userData.userId, provider.getReference())
      .then((integration) => {
        setUserIntegrationOAuthLoading(false);
        setUserIntegrationOAuth(integration);
      });
  }, []);

  return (
    <div className="card card-tall p-2 border border-info mb-1 mt-1">
      <p className="mt-0 mb-0">
        <strong>{provider.getName()}</strong>
      </p>
      {webhookDataLoading === true && <Loader size="large" className="mt-4 mb-4" />}
      {webhookDataLoading === false &&
        userIntegrationOAuthLoading === false &&
        userIntegrationOAuth &&
        webhookData && (
          <>
            {webhookData.success === true && (
              <p className="alert alert-success mb-0 mt-1">Connected.</p>
            )}
            {webhookData.success === false && (
              <p className="alert alert-warning mb-0 mt-1">
                Connection has expired.{' '}
                {userIntegrationOAuth.getAgencyAccountId() ? (
                  <>
                    Your agency account needs to be disconnected and reconnected. Your agency
                    principal can do this via the agency settings page.
                  </>
                ) : (
                  <>
                    <a href="/agent/settingsController/index/voip">
                      Disconnected and reconnected your connection via the settings page
                    </a>
                  </>
                )}
                .
              </p>
            )}
            {webhookData.success === true && (
              <>
                {provider.getReference() === 'dialpad' && (
                  <DialpadWebhookReport webhookData={webhookData} />
                )}
                {provider.getReference() === 'ringcentral' && (
                  <RingCentralWebhookReport webhookData={webhookData} />
                )}
                {provider.getReference() === 'phonecom' && (
                  <PhonecomWebhookReport webhookData={webhookData} />
                )}
              </>
            )}
          </>
        )}
    </div>
  );
}

export default function AgentLogsWebhookPanel(
  props: AgentLogsWebhookPanelProps
): React.JSX.Element {
  const [webhooksLoading, setWebhooksLoading] = React.useState<boolean>(true);
  const [webhooks, setWebhooks] = React.useState<AgentLogsWebhook[]>(null);
  const userData = React.useContext(UserContext);
  const targetIntegrations = ['ringcentral', 'phonecom', 'dialpad'];

  const userIntegrationOAuthRepository = new UserIntegrationOAuthRepository();
  userIntegrationOAuthRepository.setAccessToken(userData.userAccessToken);

  React.useEffect(() => {
    setWebhooksLoading(true);
    userIntegrationOAuthRepository
      .getUserIntegrationsOAuth(userData.userId)
      .then((integrations) => {
        setWebhooksLoading(false);
        // Filter relevant services
        const matchingIntegrations: UserIntegrationOAuth[] = integrations
          .getItems()
          .filter(
            (integration) =>
              integration.getProvider() &&
              targetIntegrations.includes(integration.getProvider().getReference())
          );

        // Turn list into just the provider objects
        const tragetIntegrations: UserIntegrationProvider[] = Array.from(
          matchingIntegrations,
          (integration) => integration.getProvider()
        );

        const integrationList = [];

        tragetIntegrations.forEach((integration) => {
          const integrationData: AgentLogsWebhook = {
            provider: integration,
          };

          integrationList.push(integrationData);
        });

        setWebhooks(integrationList);
      });
  }, []);

  return (
    <>
      <div className="card card-tall p-2">
        <div className="row">
          {webhooksLoading === true && (
            <div className="col-12">
              <Loader size="large" className="mt-4 mb-4" />
            </div>
          )}
          {webhooksLoading === false && webhooks && (
            <div className="col-12">
              {webhooks.map((webhook) => (
                <AgentLogsWebhookSingle {...webhook} />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
